import { EMPTY_CELL, Point, Rect, TRANSPARENT, rectFromPoints } from '../core/types';
import { Selection } from '../core/Selection';
import { BoxPlanner, styleWeight } from '../unicode/boxdrawing';
import { sampleGradient } from './gradient';
import {
  arrowHead,
  floodFill,
  plotBrush,
  plotEllipse,
  plotLine,
  plotPath,
  plotRect,
} from './raster';
import {
  CellWrite,
  SelectionResult,
  Tool,
  ToolEnv,
  ToolStroke,
  cellsEqualEnough,
  paintCell,
} from './types';

/** Collects writes while de-duplicating cells and honouring the selection. */
class WriteBuffer {
  private map = new Map<number, CellWrite>();

  constructor(private readonly env: ToolEnv) {}

  put(x: number, y: number, write: (existing: ReturnType<ToolEnv['read']>) => ReturnType<ToolEnv['read']>): void {
    const { doc, selection } = this.env;
    if (x < 0 || y < 0 || x >= doc.width || y >= doc.height) return;
    if (!selection.allows(x, y)) return;
    const key = y * doc.width + x;
    const existing = this.map.get(key)?.cell ?? this.env.read(x, y);
    this.map.set(key, { x, y, cell: write(existing) });
  }

  list(): CellWrite[] {
    return [...this.map.values()];
  }
}

function strokeShift(stroke: ToolStroke): { from: Point; to: Point } {
  let to = stroke.current;
  if (stroke.shift) {
    // Constrain to horizontal, vertical or 45 degrees.
    const dx = to.x - stroke.start.x;
    const dy = to.y - stroke.start.y;
    if (Math.abs(dx) > Math.abs(dy) * 2) to = { x: to.x, y: stroke.start.y };
    else if (Math.abs(dy) > Math.abs(dx) * 2) to = { x: stroke.start.x, y: to.y };
    else {
      const d = Math.min(Math.abs(dx), Math.abs(dy));
      to = { x: stroke.start.x + Math.sign(dx) * d, y: stroke.start.y + Math.sign(dy) * d };
    }
  }
  return { from: stroke.start, to };
}

/** Right-drag swaps foreground and background, as in most pixel editors. */
function effectiveBrush(stroke: ToolStroke, env: ToolEnv) {
  if (stroke.button !== 2) return env.brush;
  return { ...env.brush, fg: env.brush.bg, bg: env.brush.fg };
}

export const pencilTool: Tool = {
  id: 'pencil',
  name: 'Pencil',
  group: 'draw',
  shortcut: 'P',
  hint: 'Paint single cells. Right-drag paints with the background colour.',
  freehand: true,
  plan(stroke, env) {
    const buf = new WriteBuffer(env);
    const brush = effectiveBrush(stroke, env);
    plotPath(stroke.points, (x, y) => buf.put(x, y, (c) => paintCell(c, brush)));
    return buf.list();
  },
};

export const brushTool: Tool = {
  id: 'brush',
  name: 'Brush',
  group: 'draw',
  shortcut: 'B',
  hint: 'Paint with the selected character and brush size.',
  freehand: true,
  plan(stroke, env) {
    const buf = new WriteBuffer(env);
    const brush = effectiveBrush(stroke, env);
    plotPath(stroke.points, (x, y) => {
      plotBrush(x, y, brush.size, (px, py) => buf.put(px, py, (c) => paintCell(c, brush)));
    });
    return buf.list();
  },
};

export const eraserTool: Tool = {
  id: 'eraser',
  name: 'Eraser',
  group: 'draw',
  shortcut: 'E',
  hint: 'Clear cells back to empty. Hold Alt to clear colours only.',
  freehand: true,
  plan(stroke, env) {
    const buf = new WriteBuffer(env);
    plotPath(stroke.points, (x, y) => {
      plotBrush(x, y, env.brush.size, (px, py) =>
        buf.put(px, py, (c) =>
          stroke.alt ? { ...c, fg: TRANSPARENT, bg: TRANSPARENT, attrs: 0 } : EMPTY_CELL,
        ),
      );
    });
    return buf.list();
  },
};

export const lineTool: Tool = {
  id: 'line',
  name: 'Line',
  group: 'shape',
  shortcut: 'L',
  hint: 'Drag a straight line. Hold Shift to snap to 45°.',
  plan(stroke, env) {
    const buf = new WriteBuffer(env);
    const brush = effectiveBrush(stroke, env);
    const { from, to } = strokeShift(stroke);
    plotLine(from.x, from.y, to.x, to.y, (x, y) => buf.put(x, y, (c) => paintCell(c, brush)));
    return buf.list();
  },
};

export const rectTool: Tool = {
  id: 'rect',
  name: 'Rectangle',
  group: 'shape',
  shortcut: 'R',
  hint: 'Drag a rectangle. Hold Alt to fill.',
  plan(stroke, env) {
    const buf = new WriteBuffer(env);
    const brush = effectiveBrush(stroke, env);
    const r = rectFromPoints(stroke.start, stroke.current);
    plotRect(r.x, r.y, r.width, r.height, (x, y) => buf.put(x, y, (c) => paintCell(c, brush)), env.brush.filled || stroke.alt);
    return buf.list();
  },
};

export const ellipseTool: Tool = {
  id: 'ellipse',
  name: 'Ellipse',
  group: 'shape',
  shortcut: 'O',
  hint: 'Drag an ellipse. Hold Shift for a visually round circle, Alt to fill.',
  plan(stroke, env) {
    const buf = new WriteBuffer(env);
    const brush = effectiveBrush(stroke, env);
    let r = rectFromPoints(stroke.start, stroke.current);
    if (stroke.shift) {
      // Terminal cells are about 1:2, so a visual circle needs twice the width.
      const h = r.height;
      const w = h * 2;
      r = { x: stroke.start.x, y: stroke.start.y, width: w, height: h };
      if (stroke.current.x < stroke.start.x) r.x = stroke.start.x - w + 1;
      if (stroke.current.y < stroke.start.y) r.y = stroke.start.y - h + 1;
    }
    plotEllipse(r.x, r.y, r.width, r.height, (x, y) => buf.put(x, y, (c) => paintCell(c, brush)), env.brush.filled || stroke.alt);
    return buf.list();
  },
};

/**
 * Smart box tool.
 *
 * Instead of stamping corner glyphs, it feeds the requested edges into the
 * connectivity planner, which merges them with whatever is already on the
 * canvas — so a new box drawn across an existing one grows real ┼ and ┬
 * junctions, in the right weight, without the user choosing a glyph.
 */
export const boxTool: Tool = {
  id: 'box',
  name: 'Box',
  group: 'shape',
  shortcut: 'X',
  hint: 'Drag a box that connects to the lines it touches. Style sets light / heavy / double / rounded.',
  plan(stroke, env) {
    const brush = effectiveBrush(stroke, env);
    const weight = styleWeight(env.brush.lineStyle);
    const rounded = env.brush.lineStyle === 'rounded';
    const planner = new BoxPlanner((x, y) => env.read(x, y).char, rounded);
    const r = rectFromPoints(stroke.start, stroke.current);
    planner.addRect(r.x, r.y, r.width, r.height, weight);

    const buf = new WriteBuffer(env);
    for (const { x, y, char } of planner.resolve()) {
      buf.put(x, y, (c) => paintCell(c, brush, char));
    }
    if (env.brush.filled && r.width > 2 && r.height > 2) {
      for (let y = r.y + 1; y < r.y + r.height - 1; y++) {
        for (let x = r.x + 1; x < r.x + r.width - 1; x++) {
          buf.put(x, y, (c) => paintCell(c, brush, ' '));
        }
      }
    }
    return buf.list();
  },
};

/** A connected line that also participates in box connectivity. */
export const boxLine = (stroke: ToolStroke, env: ToolEnv): CellWrite[] => {
  const weight = styleWeight(env.brush.lineStyle);
  const rounded = env.brush.lineStyle === 'rounded';
  const planner = new BoxPlanner((x, y) => env.read(x, y).char, rounded);
  const { from, to } = strokeShift(stroke);
  // An L-shaped run: horizontal then vertical, like a routed connector.
  planner.addHorizontal(from.x, to.x, from.y, weight);
  planner.addVertical(to.x, from.y, to.y, weight);
  const buf = new WriteBuffer(env);
  const brush = effectiveBrush(stroke, env);
  for (const { x, y, char } of planner.resolve()) {
    buf.put(x, y, (c) => paintCell(c, brush, char));
  }
  return buf.list();
};

export const arrowTool: Tool = {
  id: 'arrow',
  name: 'Arrow',
  group: 'shape',
  hint: 'Drag a connector with an arrowhead at the end.',
  plan(stroke, env) {
    const writes = boxLine(stroke, env);
    const brush = effectiveBrush(stroke, env);
    const { from, to } = strokeShift(stroke);
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    if (dx === 0 && dy === 0) return writes;
    const head = arrowHead(dy !== 0 ? 0 : dx, dy);
    const existing = env.read(to.x, to.y);
    return [...writes.filter((w) => !(w.x === to.x && w.y === to.y)), {
      x: to.x,
      y: to.y,
      cell: paintCell(existing, brush, head),
    }];
  },
};

export const fillTool: Tool = {
  id: 'fill',
  name: 'Fill',
  group: 'draw',
  shortcut: 'F',
  hint: 'Flood-fill the region under the cursor.',
  instant: true,
  plan(stroke, env) {
    const buf = new WriteBuffer(env);
    const brush = effectiveBrush(stroke, env);
    const read = env.brush.sampleComposite ? env.readComposite : env.read;
    const seed = read(stroke.start.x, stroke.start.y);
    floodFill(
      env.doc.width,
      env.doc.height,
      stroke.start.x,
      stroke.start.y,
      (x, y) => env.selection.allows(x, y) && cellsEqualEnough(read(x, y), seed, env.brush.tolerance),
      (x, y) => buf.put(x, y, (c) => paintCell(c, brush)),
    );
    return buf.list();
  },
};

export const gradientTool: Tool = {
  id: 'gradient',
  name: 'Gradient',
  group: 'color',
  shortcut: 'G',
  hint: 'Drag to define the ramp. Applies to the selection, or the whole layer.',
  plan(stroke, env) {
    const buf = new WriteBuffer(env);
    const def = env.brush.gradient;
    const area = env.selection.getBounds() ?? {
      x: 0,
      y: 0,
      width: env.doc.width,
      height: env.doc.height,
    };
    for (let y = area.y; y < area.y + area.height; y++) {
      for (let x = area.x; x < area.x + area.width; x++) {
        const existing = env.read(x, y);
        // Only paint cells that have something to colour, unless the gradient
        // targets the background (where empty cells are the point).
        if (existing.char === '' && def.target === 'fg') continue;
        const color = sampleGradient(def, x, y, stroke.start, stroke.current);
        buf.put(x, y, (c) => ({
          char: c.char === '' && def.target !== 'fg' ? ' ' : c.char,
          fg: def.target === 'fg' || def.target === 'both' ? color : c.fg,
          bg: def.target === 'bg' || def.target === 'both' ? color : c.bg,
          attrs: c.attrs,
        }));
      }
    }
    return buf.list();
  },
};

export const eyedropperTool: Tool = {
  id: 'eyedropper',
  name: 'Eyedropper',
  group: 'color',
  shortcut: 'I',
  hint: 'Pick the character, colours and attributes under the cursor.',
  instant: true,
  pick(stroke, env) {
    const cell = env.readComposite(stroke.start.x, stroke.start.y);
    if (cell.char === '' && cell.fg === TRANSPARENT && cell.bg === TRANSPARENT) return null;
    return {
      char: cell.char === '' ? ' ' : cell.char,
      fg: cell.fg === TRANSPARENT ? env.brush.fg : cell.fg,
      bg: cell.bg === TRANSPARENT ? env.brush.bg : cell.bg,
      attrs: cell.attrs,
    };
  },
};

/* ------------------------------------------------------------------ *
 * Selection tools
 * ------------------------------------------------------------------ */

function selectionMode(stroke: ToolStroke): SelectionResult['mode'] {
  if (stroke.shift) return 'add';
  if (stroke.alt) return 'subtract';
  return 'replace';
}

export const selectTool: Tool = {
  id: 'select',
  name: 'Select',
  group: 'select',
  shortcut: 'V',
  hint: 'Drag a rectangular selection. Shift adds, Alt subtracts.',
  planSelection(stroke, env) {
    const s = new Selection(env.doc.width, env.doc.height);
    s.setRect(rectFromPoints(stroke.start, stroke.current));
    return { selection: s, mode: selectionMode(stroke) };
  },
};

export const lassoTool: Tool = {
  id: 'lasso',
  name: 'Lasso',
  group: 'select',
  hint: 'Draw a freehand selection outline.',
  freehand: true,
  planSelection(stroke, env) {
    if (stroke.points.length < 3) return null;
    return {
      selection: Selection.fromPolygon(env.doc.width, env.doc.height, stroke.points),
      mode: selectionMode(stroke),
    };
  },
};

export const wandTool: Tool = {
  id: 'wand',
  name: 'Magic Wand',
  group: 'select',
  hint: 'Select the connected region of matching cells.',
  instant: true,
  planSelection(stroke, env) {
    const grid = env.brush.sampleComposite ? env.doc.composite() : env.layer.grid;
    return {
      selection: Selection.connectedRegion(grid, stroke.start.x, stroke.start.y, (a, b) =>
        cellsEqualEnough(a, b, env.brush.tolerance),
      ),
      mode: selectionMode(stroke),
    };
  },
};

/**
 * Move tool.
 *
 * Lifts the selected cells (or the whole layer) and re-stamps them at the drag
 * offset. The lifted source is cleared unless Alt is held, which copies.
 */
/**
 * The block a move operates on.
 *
 * With a selection it is the selection bounds; without one it is the painted
 * content of the layer, so "move" with nothing selected slides the artwork
 * rather than shifting an ocean of empty cells.
 */
export function moveArea(env: ToolEnv): Rect {
  return (
    env.selection.getBounds() ??
    env.layer.grid.contentBounds() ?? {
      x: 0,
      y: 0,
      width: env.doc.width,
      height: env.doc.height,
    }
  );
}

/**
 * Offset for a move, clamped so the moved block cannot leave the document.
 *
 * Clamping rather than clipping is deliberate: dragging past the edge used to
 * clear the source cells and then silently drop the destination writes, which
 * destroyed content. Now the block simply stops at the edge.
 */
export function moveDelta(stroke: ToolStroke, env: ToolEnv): { dx: number; dy: number; area: Rect } {
  const area = moveArea(env);
  const rawX = stroke.current.x - stroke.start.x;
  const rawY = stroke.current.y - stroke.start.y;
  const dx = Math.max(-area.x, Math.min(env.doc.width - (area.x + area.width), rawX));
  const dy = Math.max(-area.y, Math.min(env.doc.height - (area.y + area.height), rawY));
  return { dx, dy, area };
}

export const moveTool: Tool = {
  id: 'move',
  name: 'Move',
  group: 'select',
  shortcut: 'M',
  hint: 'Drag the selection (or the whole drawing). Hold Alt to copy instead of move.',
  plan(stroke, env) {
    const { dx, dy, area } = moveDelta(stroke, env);
    if (dx === 0 && dy === 0) return [];

    const writes = new Map<number, CellWrite>();
    const key = (x: number, y: number) => y * env.doc.width + x;

    // Lift: clear the source first, so the destination pass can overwrite the
    // clears where the block overlaps its own origin.
    if (!stroke.alt) {
      for (let y = area.y; y < area.y + area.height; y++) {
        for (let x = area.x; x < area.x + area.width; x++) {
          if (!env.selection.allows(x, y)) continue;
          writes.set(key(x, y), { x, y, cell: EMPTY_CELL });
        }
      }
    }

    // Stamp. Empty source cells stay transparent instead of erasing whatever
    // they land on, matching how layers composite everywhere else.
    for (let y = area.y; y < area.y + area.height; y++) {
      for (let x = area.x; x < area.x + area.width; x++) {
        if (!env.selection.allows(x, y)) continue;
        const cell = env.read(x, y);
        if (cell.char === '' && cell.bg === TRANSPARENT && cell.fg === TRANSPARENT) continue;
        const tx = x + dx;
        const ty = y + dy;
        if (tx < 0 || ty < 0 || tx >= env.doc.width || ty >= env.doc.height) continue;
        writes.set(key(tx, ty), { x: tx, y: ty, cell });
      }
    }
    return [...writes.values()];
  },
};
